import React, { useState, useEffect } from 'react';
import { Webhook } from '../../types/webhook';
import { getWebhooks, deleteWebhook } from '../../services/webhookService';
import { useToast } from '../../hooks/use-toast';
import { CreateWebhookDialog } from './CreateWebhookDialog';
import { WebhooksList } from './WebhooksList';
import { Loader2, Zap, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

export const WebhooksSection: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWebhooks();
      setWebhooks(data);
    } catch (error) {
      console.error('Error loading webhooks:', error);
      setError(error instanceof Error ? error.message : 'Failed to load webhooks');
      toast({
        title: 'Error',
        description: 'Failed to load webhooks',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWebhooks();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      await deleteWebhook(id);
      toast({
        title: 'Webhook Deleted',
        description: 'The webhook has been deleted successfully',
      });
      await loadWebhooks();
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete webhook',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="dashboard-card p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
              <Zap className="w-5 h-5" />
              <span>Webhooks</span>
            </h3>
            <p className="text-muted-foreground text-sm">
              Configure webhooks to receive real-time notifications when events occur in your workspace.
            </p>
          </div>
          <CreateWebhookDialog
            onSuccess={loadWebhooks}
            trigger={
              <Button>
                <Zap className="w-4 h-4 mr-2" />
                Create Webhook
              </Button>
            }
          />
        </div>

        {/* Info Banner */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">Webhook Events</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>Create:</strong> Triggered when a new record is created</li>
                <li><strong>Update:</strong> Triggered when an existing record is updated</li>
                <li><strong>Delete:</strong> Triggered when a record is deleted</li>
              </ul>
              <p className="mt-2 text-xs">
                All webhook payloads include a signature header for verification.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      <div className="dashboard-card p-6">
        <h4 className="font-semibold mb-4">Your Webhooks</h4>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading webhooks...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive opacity-50" />
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={loadWebhooks} variant="outline">
              Try Again
            </Button>
          </div>
        ) : (
          <WebhooksList
            webhooks={webhooks}
            onDelete={handleDelete}
            onUpdate={loadWebhooks}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};
